import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Check, ExternalLink, Mail, ShoppingBag } from 'lucide-react';
import { Button } from '../components/ui/button';
import MerchHeader from '../components/MerchHeader';
import Footer from '../components/Footer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAYPAL_HOSTED_BUTTON_ID = 'GKVW63N7PCF6U';

const OrderSuccess = () => {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/orders/${orderId}`)
      .then((res) => setOrder(res.data))
      .catch(() => setOrder(null))
      .finally(() => setLoading(false));
  }, [orderId]);

  const paypalUrl = order
    ? `https://www.paypal.com/donate/?${new URLSearchParams({
        hosted_button_id: PAYPAL_HOSTED_BUTTON_ID,
        item_name: `ACT Merch Order #${order.id.slice(0, 8)}`,
        custom: `Order ${order.id}`,
      }).toString()}`
    : '';

  return (
    <div className="min-h-screen bg-[#f8f4ee]">
      <MerchHeader />
      <main className="max-w-[760px] mx-auto px-4 md:px-6 py-16">
        {loading ? (
          <div className="text-center text-[#5a5a5a]">Loading your order…</div>
        ) : !order ? (
          <div className="bg-white rounded-2xl border border-black/5 shadow-sm p-10 text-center">
            <h1 className="text-3xl text-[#0a1030]" style={{ fontFamily: 'Fraunces, serif', fontWeight: 500 }}>
              Order not found
            </h1>
            <Button asChild className="mt-6 rounded-full bg-[#0a1030] hover:bg-[#111a4a] text-white">
              <Link to="/merch">Back to shop</Link>
            </Button>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-black/5 shadow-sm p-8 md:p-12">
            <div className="text-center">
              <div className="mx-auto h-14 w-14 rounded-full bg-[#0a1030] text-white flex items-center justify-center mb-5">
                <Check className="h-7 w-7" strokeWidth={3} />
              </div>
              <h1 className="text-[32px] md:text-[42px] text-[#0a1030] leading-tight" style={{ fontFamily: 'Fraunces, serif', fontWeight: 500 }}>
                Order placed, {order.customer_name.split(' ')[0]}!
              </h1>
              <p className="mt-2 text-sm text-[#5a5a5a]">
                Order #<span className="font-mono">{order.id.slice(0, 8).toUpperCase()}</span>
              </p>
            </div>

            <div className="mt-8 bg-[#faf7f2] rounded-xl p-5 border border-[#e9d9a0]/40">
              <div className="flex items-start gap-3">
                <div className="h-9 w-9 rounded-full bg-[#0a1030] text-[#e9d9a0] flex items-center justify-center flex-shrink-0">
                  <ExternalLink className="h-4 w-4" />
                </div>
                <div>
                  <div className="font-semibold text-[#0a1030]">Complete your payment</div>
                  <p className="text-sm text-[#3a3a3a] mt-1">
                    Your PayPal tab should have opened. If not, tap the button below.
                    Enter{' '}
                    <span className="font-semibold">${order.subtotal.toFixed(2)}</span>{' '}
                    as your payment amount and include{' '}
                    <span className="font-mono text-xs bg-white px-1.5 py-0.5 rounded">
                      Order {order.id.slice(0, 8).toUpperCase()}
                    </span>{' '}
                    in the note.
                  </p>
                  <Button
                    asChild
                    className="mt-4 rounded-full bg-[#1c2b6b] hover:bg-[#141f52] text-white h-11 px-6"
                  >
                    <a href={paypalUrl} target="_blank" rel="noopener noreferrer">
                      Pay ${order.subtotal.toFixed(2)} on PayPal
                    </a>
                  </Button>
                </div>
              </div>
            </div>

            {/* Items */}
            <div className="mt-8">
              <div className="text-[13px] font-semibold text-[#0a1030] mb-3 uppercase tracking-wider">
                Your order
              </div>
              <div className="divide-y divide-black/5">
                {order.items.map((i, idx) => (
                  <div key={idx} className="py-3 flex justify-between text-sm">
                    <div>
                      <div className="font-medium text-[#0a1030]">{i.product_name}</div>
                      <div className="text-xs text-[#7a7a7a]">Size {i.size} · Qty {i.quantity}</div>
                    </div>
                    <div className="font-semibold text-[#0a1030]">
                      ${(i.price * i.quantity).toFixed(2)}
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex justify-between pt-3 mt-2 border-t border-black/10 text-[16px] font-semibold text-[#0a1030]">
                <span>Total</span>
                <span>${order.subtotal.toFixed(2)}</span>
              </div>
            </div>

            {/* Next steps */}
            <div className="mt-8 pt-8 border-t border-black/10">
              <div className="text-[13px] font-semibold text-[#0a1030] mb-3 uppercase tracking-wider">
                Pickup
              </div>
              <p className="text-sm text-[#3a3a3a] leading-relaxed">
                Once your PayPal payment is confirmed, we'll get your order
                ready. We'll email <span className="font-semibold">{order.customer_email}</span>{' '}
                when it's ready for pickup at{' '}
                <span className="font-semibold">370 Tomkins Court, Gilroy CA</span>{' '}
                — usually the following Sunday.
              </p>
              <div className="mt-6 flex flex-col sm:flex-row gap-3">
                <Button
                  asChild
                  variant="outline"
                  className="rounded-full border-[#0a1030] text-[#0a1030] hover:bg-[#0a1030]/5"
                >
                  <Link to="/merch"><ShoppingBag className="h-4 w-4 mr-2" /> Keep shopping</Link>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  className="rounded-full border-[#0a1030] text-[#0a1030] hover:bg-[#0a1030]/5"
                >
                  <a href="mailto:info@actgilroy.org?subject=Order%20Question">
                    <Mail className="h-4 w-4 mr-2" /> Contact us
                  </a>
                </Button>
              </div>
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default OrderSuccess;
